import React, { useState, useEffect } from 'react';
import PlaidLinkComponent from './PlaidLinkComponent';
import PlaidAccountsModal from './PlaidAccountsModal';
import AddPropertyModal from './AddPropertyModal';
import { getProperties } from '../api/properties';
import apiClient from '../api/client';
import './AddAccountModal.css';

const AddAccountModal = ({ isOpen, onClose, onSnapTradeOpen, onPropertySave, username }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showPlaidAccountsModal, setShowPlaidAccountsModal] = useState(false);
  
  // Debt form state
  const [debtFormData, setDebtFormData] = useState({
    linkedProperty: '',
    debtName: '',
    originalDebtAmount: '',
    currentDebtBalance: ''
  });
  const [debtErrors, setDebtErrors] = useState({});
  const [properties, setProperties] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const accountOptions = [
    {
      id: 'plaid',
      title: 'Connect Brokerage Accounts',
      description: 'Securely connect your investment accounts with Plaid',
      icon: '🏦',
      color: '#2A9D8F',
      action: 'plaid'
    },
    {
      id: 'brokerage',
      title: 'Connect Brokerage Accounts',
      description: 'Securely connect your investment accounts with SnapTrade',
      icon: '📈',
      color: '#E76F51',
      action: 'snapTrade'
    },
    {
      id: 'property',
      title: 'Add Property',
      description: 'Add real estate and property investments',
      icon: '🏠',
      color: '#F4A261',
      action: 'property'
    },
    {
      id: 'debt',
      title: 'Add Debt/Loan',
      description: 'Track your loans and debt obligations',
      icon: '📉',
      color: '#264653',
      action: 'debt'
    }
  ];

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    
    if (option.action === 'plaid') {
      // Show Plaid connection interface
    } else if (option.action === 'snapTrade') {
      onSnapTradeOpen();
      onClose();
    } else if (option.action === 'property') {
      setShowPropertyModal(true);
    } else if (option.action === 'debt') {
      // Show debt form inline and fetch properties
      fetchProperties();
    } else if (option.action === 'other') {
      console.log('Other account type selected');
      onClose();
    }
  };

  const handlePlaidSuccess = (result) => {
    console.log('Plaid account connected:', result);
    if (onPropertySave) {
      onPropertySave();
    }
  };

  const handlePropertySave = (propertyData) => {
    if (onPropertySave) {
      onPropertySave(propertyData);
    }
    setShowPropertyModal(false);
    setSelectedOption(null);
    onClose();
  };

  const handlePropertyClose = () => {
    setShowPropertyModal(false);
    setSelectedOption(null);
  };

  const handleManagePlaidAccounts = () => {
    setShowPlaidAccountsModal(true);
  };

  const handlePlaidAccountsClose = () => {
    setShowPlaidAccountsModal(false);
  };

  const handlePlaidAccountConnected = () => {
    if (onPropertySave) {
      onPropertySave();
    }
  };

  const fetchProperties = async () => {
    try {
      const data = await getProperties();
      setProperties(data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setProperties([]);
    }
  };

  // Debt form handlers
  const handleDebtInputChange = (e) => {
    const { name, value } = e.target;
    setDebtFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (debtErrors[name]) {
      setDebtErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateDebtForm = () => {
    const newErrors = {};

    if (!debtFormData.debtName.trim()) {
      newErrors.debtName = 'Debt name is required';
    }

    if (!debtFormData.originalDebtAmount || isNaN(parseFloat(debtFormData.originalDebtAmount)) || parseFloat(debtFormData.originalDebtAmount) <= 0) {
      newErrors.originalDebtAmount = 'Please enter a valid original debt amount';
    }

    if (!debtFormData.currentDebtBalance || isNaN(parseFloat(debtFormData.currentDebtBalance)) || parseFloat(debtFormData.currentDebtBalance) < 0) {
      newErrors.currentDebtBalance = 'Please enter a valid current debt balance';
    }

    setDebtErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDebtSubmit = async (e) => {
    e.preventDefault();
    
    if (validateDebtForm()) {
      setIsSubmitting(true);
      
      try {
        const debtPayload = {
          debtName: debtFormData.debtName,
          originalDebtAmount: parseFloat(debtFormData.originalDebtAmount),
          currentDebtBalance: parseFloat(debtFormData.currentDebtBalance),
          debtType: 'debt', // Default to mortgage, could be made configurable
          propertyId: debtFormData.linkedProperty ? parseInt(debtFormData.linkedProperty) : null
        };

        const { data: result } = await apiClient.post('/api/v1/debts', debtPayload);
        if (onPropertySave) {
          onPropertySave(result);
        }
        setSelectedOption(null);
        onClose();
      } catch (error) {
        console.error('Error saving debt:', error);
        setDebtErrors({ submit: 'Network error. Please check your connection and try again.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const resetDebtForm = () => {
    setDebtFormData({
      linkedProperty: '',
      debtName: '',
      originalDebtAmount: '',
      currentDebtBalance: ''
    });
    setDebtErrors({});
  };

  // Reset selectedOption when modal closes
  // Fetch properties when debt form is selected
  useEffect(() => {
    if (selectedOption?.action === "debt") {
      fetchProperties();
    }
  }, [selectedOption]);  useEffect(() => {
    if (!isOpen) {
      setSelectedOption(null);
      resetDebtForm();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Add New Account</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          
          <div className="modal-body">
            {selectedOption ? (
              <div className="selected-option-content">
                {selectedOption.action === 'plaid' ? (
                  <div className="plaid-connection-section">
                    <PlaidLinkComponent 
                      username={username}
                      onSuccess={handlePlaidSuccess}
                      onClose={onClose}
                    />
                    
                    <div className="plaid-manage-section">
                      <button 
                        className="manage-accounts-btn"
                        onClick={handleManagePlaidAccounts}
                      >
                        <span>⚙️</span>
                        Manage Connected Accounts
                      </button>
                    </div>
                  </div>
                ) : selectedOption.action === 'debt' ? (
                  <div className="debt-form-section">
                    <div className="form-header">
                      <div className="option-icon-large" style={{ backgroundColor: selectedOption.color }}>
                        {selectedOption.icon}
                      </div>
                      <h3>{selectedOption.title}</h3>
                      <p>Complete your debt information below</p>
                    </div>
                    
                    <form onSubmit={handleDebtSubmit} className="debt-form">
                      <div className="form-group">
                        <label htmlFor="linkedProperty">Link to a Property (Optional)</label>
                        <select
                          id="linkedProperty"
                          name="linkedProperty"
                          value={debtFormData.linkedProperty}
                          onChange={handleDebtInputChange}
                          className="form-select"
                        >
                          <option value="">Select a property (optional)</option>
                          {properties.map((property) => (
                            <option key={property.id} value={property.id}>
                              {property.propertyName} - ${property.propertyValue?.toLocaleString()}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="debtName">Debt Name *</label>
                        <input
                          type="text"
                          id="debtName"
                          name="debtName"
                          value={debtFormData.debtName}
                          onChange={handleDebtInputChange}
                          className={`form-input ${debtErrors.debtName ? 'error' : ''}`}
                          placeholder="e.g., Mortgage, Car Loan, Credit Card"
                        />
                        {debtErrors.debtName && <span className="error-message">{debtErrors.debtName}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="originalDebtAmount">Original Debt Amount *</label>
                        <div className="currency-input">
                          <span className="currency-symbol">$</span>
                          <input
                            type="number"
                            id="originalDebtAmount"
                            name="originalDebtAmount"
                            value={debtFormData.originalDebtAmount}
                            onChange={handleDebtInputChange}
                            className={`form-input ${debtErrors.originalDebtAmount ? 'error' : ''}`}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                        </div>
                        {debtErrors.originalDebtAmount && <span className="error-message">{debtErrors.originalDebtAmount}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="currentDebtBalance">Current Debt Balance *</label>
                        <div className="currency-input">
                          <span className="currency-symbol">$</span>
                          <input
                            type="number"
                            id="currentDebtBalance"
                            name="currentDebtBalance"
                            value={debtFormData.currentDebtBalance}
                            onChange={handleDebtInputChange}
                            className={`form-input ${debtErrors.currentDebtBalance ? 'error' : ''}`}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                        </div>
                        {debtErrors.currentDebtBalance && <span className="error-message">{debtErrors.currentDebtBalance}</span>}
                      </div>

                      {debtErrors.submit && (
                        <div className="form-group">
                          <span className="error-message">{debtErrors.submit}</span>
                        </div>
                      )}

                      <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" style={{ backgroundColor: selectedOption.color }} disabled={isSubmitting}>
                          {isSubmitting ? 'Saving...' : 'Add Debt'}
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="option-placeholder">
                    <div className="option-icon-large" style={{ backgroundColor: selectedOption.color }}>
                      {selectedOption.icon}
                    </div>
                    <h3>{selectedOption.title}</h3>
                    <p>This feature is coming soon!</p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <p className="modal-description">
                  Choose the type of account you'd like to add to your portfolio
                </p>
                
                <div className="options-grid">
                  {accountOptions.map((option) => (
                    <div
                      key={option.id}
                      className={`option-card ${selectedOption?.id === option.id ? 'selected' : ''}`}
                      onClick={() => handleOptionSelect(option)}
                      style={{ '--option-color': option.color }}
                    >
                      <div className="option-icon" style={{ backgroundColor: option.color }}>
                        {option.icon}
                      </div>
                      <div className="option-content">
                        <h3>{option.title}</h3>
                        <p>{option.description}</p>
                      </div>
                      <div className="option-arrow">→</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          
          <div className="modal-footer" style={{ display: selectedOption?.action === 'debt' ? 'none' : 'flex' }}>
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Property Modal */}
      <AddPropertyModal
        isOpen={showPropertyModal}
        onClose={handlePropertyClose}
        onSave={handlePropertySave}
      />

      {/* Plaid Accounts Management Modal */}
      <PlaidAccountsModal
        isOpen={showPlaidAccountsModal}
        onClose={handlePlaidAccountsClose}
        username={username}
        onAccountConnected={handlePlaidAccountConnected}
      />
    </>
  );
};

export default AddAccountModal;
